import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Field, useForm, useFormState } from 'react-final-form';

import useId8AgentOptions from '../../../hooks/useId8AgentOptions';
import WithLabel, { LABEL_STYLES } from '../../../components/with-label/WithLabel';
import WithError from '../../../components/with-error/WithError';
import CommonDropdown from '../../../components/common-dropdown/CommonDropdown';
import CommonTextarea from '../../../components/common-textarea/CommonTextarea';

import scss from './Id8Creation.module.scss';

const Id8AgentFields = ({ isLoading }) => {
  const { t } = useTranslation('projects');

  const { change } = useForm();
  const { values, initialValues } = useFormState();

  const { agentsOptions, CUSTOM_AGENT_OPTION, NONE_AGENT_OPTION, isLoading: isLoadingAgents } = useId8AgentOptions();

  const selectedAgent = useMemo(
    () => [...agentsOptions, CUSTOM_AGENT_OPTION, NONE_AGENT_OPTION].find((agent) => agent.value === values.agentId),
    [agentsOptions, CUSTOM_AGENT_OPTION, NONE_AGENT_OPTION, values.agentId],
  );

  const handleChangeAgent = (agentId) => {
    const agent = [CUSTOM_AGENT_OPTION, ...agentsOptions].find((agent) => agent.value === agentId);

    if (!agent) return;

    change('agentType', agent.type);
    change('agentId', agent.value);
    change('agentReferenceId', '');

    change('instructions', agent.instructions);
  };

  const handleChangeAgentReference = (agentId) => {
    const agent = [NONE_AGENT_OPTION, ...agentsOptions].find((agent) => agent.value === agentId);

    if (!agent) return;

    change('agentReferenceId', agent.value);
    change('instructions', agent.instructions ? agent.instructions : initialValues.instructions);
  };

  return (
    <>
      <Field name='agentId'>
        {({ input, meta }) => (
          <WithLabel label={`${t('agent')}:`} variant={LABEL_STYLES.CUSTOM} isBold extraClassName={scss.inputRow}>
            <WithError isShowError={meta.touched && meta.error} errorText={t(meta.error)} isAbsolute>
              <CommonDropdown
                options={[CUSTOM_AGENT_OPTION, ...agentsOptions]}
                value={input.value}
                onSelect={handleChangeAgent}
                onBlur={input.onBlur}
                onFocus={input.onFocus}
                name={input.name}
                size='sm'
                disabled={isLoading || isLoadingAgents}
                isLoading={isLoadingAgents}
                isError={meta.touched && meta.error}
              />
            </WithError>
          </WithLabel>
        )}
      </Field>

      {selectedAgent?.value === CUSTOM_AGENT_OPTION.value ? (
        <>
          <Field name='agentReferenceId'>
            {({ input }) => (
              <WithLabel
                label={`${t('agentReference')}:`}
                variant={LABEL_STYLES.CUSTOM}
                isBold
                extraClassName={scss.inputRow}>
                <CommonDropdown
                  options={[NONE_AGENT_OPTION, ...agentsOptions]}
                  value={input.value}
                  onSelect={handleChangeAgentReference}
                  name={input.name}
                  size='sm'
                  disabled={isLoading || isLoadingAgents}
                  isLoading={isLoadingAgents}
                />
              </WithLabel>
            )}
          </Field>

          <Field name='instructions'>
            {({ meta, input }) => (
              <WithLabel
                variant={LABEL_STYLES.CUSTOM}
                label={`${t('instructions')}:`}
                isBold
                extraClassName={scss.inputRowLong}>
                <WithError isShowError={meta.touched && meta.error} errorText={t(meta.error)} isAbsolute>
                  <CommonTextarea
                    name={input.name}
                    value={input.value}
                    onChange={input.onChange}
                    onBlur={input.onBlur}
                    onFocus={input.onFocus}
                    autoComplete={input.autoComplete}
                    isError={meta.touched && meta.error}
                    disabled={isLoading}
                    rows='5'
                  />
                </WithError>
              </WithLabel>
            )}
          </Field>
        </>
      ) : null}
    </>
  );
};

export default Id8AgentFields;
